import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Clock, 
  CheckCircle, 
  Calendar,
  Star,
  Users,
  Zap,
  Shield,
  ArrowRight,
  Phone,
  Car,
  Target,
  Award,
  MapPin
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import usePageContent from '@/hooks/usePageContent';

const IntensivkursSeite = () => {
  const { t: c } = usePageContent('intensivkurs_7tage');
  useEffect(() => {
    document.title = 'Führerschein in 7 Tagen | Intensivkurs Frankfurt & Offenbach | Fahrschule Rößner';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute('content', 'Führerschein in nur 7 Tagen! Intensivkurs bei Fahrschule Rößner in Frankfurt, Offenbach & Bad Homburg. Schnell, effektiv, mit Bestehensgarantie. Jetzt Platz sichern!');
    }
    window.scrollTo(0, 0);
  }, []);

  const tagesablauf = [
    {
      tag: c('tag1_tag', 'Tag 1-2'),
      titel: c('tag1_titel', 'Theorie-Crashkurs'),
      beschreibung: c('tag1_desc', 'Intensiver Theorieunterricht mit allen prüfungsrelevanten Themen. Du lernst alle 12 Grundstoff-Lektionen in kompakter Form.'),
      icon: "📚"
    },
    {
      tag: c('tag2_tag', 'Tag 3'),
      titel: c('tag2_titel', 'Theorieprüfung'),
      beschreibung: c('tag2_desc', 'Nach optimaler Vorbereitung legst du deine Theorieprüfung beim TÜV ab. Unsere Bestehensquote: über 95%!'),
      icon: "✅"
    },
    {
      tag: c('tag3_tag', 'Tag 4-6'),
      titel: c('tag3_titel', 'Intensive Fahrstunden'),
      beschreibung: c('tag3_desc', 'Täglich 4-6 Fahrstunden. Stadt, Autobahn, Nachtfahrt - du sammelst schnell Routine und Sicherheit.'),
      icon: "🚗"
    },
    {
      tag: c('tag4_tag', 'Tag 7'),
      titel: c('tag4_titel', 'Praktische Prüfung'),
      beschreibung: c('tag4_desc', 'Der große Tag! Mit optimaler Vorbereitung und unserer Prüfungs-Testfahrt gehst du selbstsicher in die Prüfung.'),
      icon: "🎯"
    }
  ];

  const vorteile = [
    {
      icon: <Zap className="w-6 h-6" />,
      titel: c('vorteil1_titel', 'Schneller am Ziel'),
      beschreibung: c('vorteil1_desc', 'Während andere monatelang warten, hast du deinen Führerschein in einer Woche.')
    },
    {
      icon: <Target className="w-6 h-6" />,
      titel: c('vorteil2_titel', 'Volle Konzentration'),
      beschreibung: c('vorteil2_desc', 'Keine Unterbrechungen, kein Vergessen zwischen den Stunden. Du bleibst im Flow.')
    },
    {
      icon: <Shield className="w-6 h-6" />,
      titel: c('vorteil3_titel', 'Höhere Bestehensquote'),
      beschreibung: c('vorteil3_desc', '95% unserer Intensivkurs-Teilnehmer bestehen beim ersten Mal.')
    },
    {
      icon: <Clock className="w-6 h-6" />,
      titel: c('vorteil4_titel', 'Zeit sparen'),
      beschreibung: c('vorteil4_desc', 'Perfekt für Berufstätige: 1 Woche Urlaub statt monatelangem Pendeln zur Fahrschule.')
    },
    {
      icon: <Award className="w-6 h-6" />,
      titel: c('vorteil5_titel', 'Erfahrene Fahrlehrer'),
      beschreibung: c('vorteil5_desc', 'Unsere Intensivkurs-Experten mit über 30 Jahren Erfahrung begleiten dich.')
    },
    {
      icon: <Car className="w-6 h-6" />,
      titel: c('vorteil6_titel', 'Moderne Fahrzeuge'),
      beschreibung: c('vorteil6_desc', 'BMW Fahrzeuge mit Automatik oder Schaltung - du entscheidest.')
    }
  ];

  const zielgruppen = [
    {
      titel: c('ziel1_titel', 'Berufstätige'),
      beschreibung: c('ziel1_desc', 'Eine Woche Urlaub nehmen und mit dem Führerschein zurückkommen.'),
      icon: "💼"
    },
    {
      titel: c('ziel2_titel', 'Studenten'),
      beschreibung: c('ziel2_desc', 'In den Semesterferien schnell den Führerschein machen.'),
      icon: "🎓"
    },
    {
      titel: c('ziel3_titel', 'Eilige'),
      beschreibung: c('ziel3_desc', 'Du brauchst den Führerschein schnell für Job oder Ausbildung.'),
      icon: "⚡"
    },
    {
      titel: c('ziel4_titel', 'Umsteiger'),
      beschreibung: c('ziel4_desc', 'Ausländischen Führerschein umschreiben mit Auffrischung.'),
      icon: "🔄"
    }
  ];

  const faqs = [
    {
      frage: c('faq1_frage', 'Schaffe ich den Führerschein wirklich in 7 Tagen?'),
      antwort: c('faq1_antwort', 'Ja! Bei uns haben über 2.000 Teilnehmer den Intensivkurs erfolgreich absolviert. Mit unserer strukturierten Methode und deiner vollen Konzentration ist es absolut machbar. 95% bestehen beim ersten Mal.')
    },
    {
      frage: c('faq2_frage', 'Was kostet der Intensivkurs?'),
      antwort: c('faq2_antwort', 'Der Intensivkurs kostet zwischen 2.200€ und 2.800€ je nach Anzahl der benötigten Fahrstunden. Im Preis enthalten: Alle Theoriestunden, Lehrmaterialien, Fahrstunden und Prüfungsgebühren. Keine versteckten Kosten!')
    },
    {
      frage: c('faq3_frage', 'Muss ich etwas vorbereiten?'),
      antwort: c('faq3_antwort', 'Ideal ist es, wenn du vorher schon mit der Lern-App übst. Wir geben dir Zugang zu unserer digitalen Lernplattform, sobald du dich anmeldest. So kannst du optimal vorbereitet starten.')
    },
    {
      frage: c('faq4_frage', 'Was passiert, wenn ich nicht bestehe?'),
      antwort: c('faq4_antwort', 'Das kommt selten vor (unter 5%), aber falls doch: Du bekommst kostenlose Nachschulung und wir organisieren schnellstmöglich einen neuen Prüfungstermin. Du zahlst nur die TÜV-Gebühr für die Wiederholungsprüfung.')
    },
    {
      frage: c('faq5_frage', 'Kann ich auch Automatik fahren?'),
      antwort: c('faq5_antwort', 'Ja! Du kannst zwischen Schaltung und Automatik wählen. Bei Automatik ist der Kurs oft sogar etwas kürzer, da du dich voll auf den Verkehr konzentrieren kannst.')
    },
    {
      frage: c('faq6_frage', 'Wann finden die Kurse statt?'),
      antwort: c('faq6_antwort', 'Wir bieten Intensivkurse jeden Monat an. Die Starttermine sind flexibel. Ruf uns an oder schreib uns - wir finden garantiert einen Termin, der zu dir passt.')
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 py-20 lg:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-grid-dark opacity-20"></div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-blue-500/30 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-emerald-500/20 rounded-full blur-3xl"></div>
        
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500/20 text-emerald-400 rounded-full text-sm font-bold mb-6">
                <Zap className="w-4 h-4" />
                EXPRESS-FÜHRERSCHEIN
              </span>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-white mb-6 leading-tight">
                Führerschein in
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-blue-400">
                  {c('hero_highlight', 'nur 7 Tagen')}
                </span>
              </h1>
              
              <p className="text-xl text-slate-300 mb-8 leading-relaxed" dangerouslySetInnerHTML={{ __html: c('hero_subtitle', 'Keine Lust auf monatelanges Warten? Mit unserem Intensivkurs hast du deinen Führerschein in einer Woche. <strong class="text-white">Garantiert.</strong>') }} />
              
              <div className="flex flex-wrap gap-4 mb-8">
                <div className="flex items-center gap-2 text-emerald-400">
                  <CheckCircle className="w-5 h-5" />
                  <span>95% Bestehensquote</span>
                </div>
                <div className="flex items-center gap-2 text-emerald-400">
                  <CheckCircle className="w-5 h-5" />
                  <span>Über 30 Jahre Erfahrung</span>
                </div>
                <div className="flex items-center gap-2 text-emerald-400">
                  <CheckCircle className="w-5 h-5" />
                  <span>4 Standorte</span>
                </div>
              </div>
              
              <div className="flex flex-wrap gap-4">
                <Link to="/kontakt">
                  <Button size="lg" className="bg-emerald-500 hover:bg-emerald-600 text-white text-lg px-8 py-6 rounded-full shadow-lg shadow-emerald-500/30">
                    Jetzt Platz sichern
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <a href="tel:+496920326059">
                  <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10 text-lg px-8 py-6 rounded-full">
                    <Phone className="mr-2 w-5 h-5" />
                    069 20 32 60 59
                  </Button>
                </a>
              </div>
            </div>
            
            <div className="relative hidden lg:block">
              <div className="relative bg-white/10 backdrop-blur-sm rounded-3xl p-8 border border-white/20">
                <div className="text-center mb-6">
                  <div className="text-6xl font-black text-white mb-2">7</div>
                  <div className="text-xl text-slate-300">Tage bis zum Führerschein</div>
                </div>
                <div className="space-y-4">
                  {tagesablauf.map((tag, index) => (
                    <div key={index} className="flex items-center gap-4 p-3 bg-white/5 rounded-xl">
                      <span className="text-2xl">{tag.icon}</span>
                      <div>
                        <div className="text-emerald-400 font-bold text-sm">{tag.tag}</div>
                        <div className="text-white font-medium">{tag.titel}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof Bar */}
      <section className="bg-slate-100 py-6 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16">
            <div className="text-center">
              <div className="text-3xl font-black text-slate-900">2.000+</div>
              <div className="text-sm text-slate-600">Intensivkurs-Absolventen</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-black text-emerald-600">95%</div>
              <div className="text-sm text-slate-600">Bestehensquote</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-black text-slate-900">30+</div>
              <div className="text-sm text-slate-600">Jahre Erfahrung</div>
            </div>
            <div className="flex items-center gap-1">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
              ))}
              <span className="ml-2 text-sm text-slate-600">4.9/5 Bewertung</span>
            </div>
          </div>
        </div>
      </section>

      {/* Ablauf Section */}
      <section className="py-20 bg-white" id="ablauf">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-bold mb-4">
              SO FUNKTIONIERT'S
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              Dein Weg zum Führerschein in 7 Tagen
            </h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              Ein klarer Plan, keine Überraschungen. So läuft dein Intensivkurs ab.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {tagesablauf.map((tag, index) => (
              <div key={index} className="relative">
                {index < tagesablauf.length - 1 && (
                  <div className="hidden lg:block absolute top-12 left-full w-full h-0.5 bg-gradient-to-r from-blue-500 to-transparent z-0"></div>
                )}
                <Card className="relative z-10 h-full border-2 border-slate-100 hover:border-blue-500 transition-colors">
                  <CardContent className="p-6">
                    <div className="text-4xl mb-4">{tag.icon}</div>
                    <div className="text-blue-600 font-bold mb-2">{tag.tag}</div>
                    <h3 className="text-xl font-bold text-slate-900 mb-3">{tag.titel}</h3>
                    <p className="text-slate-600">{tag.beschreibung}</p>
                  </CardContent>
                </Card>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Vorteile Section */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 bg-emerald-100 text-emerald-700 rounded-full text-sm font-bold mb-4">
              WARUM INTENSIVKURS?
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              6 Gründe für deinen Turbo-Führerschein
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {vorteile.map((vorteil, index) => (
              <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-shadow">
                <CardContent className="p-8">
                  <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-emerald-500 rounded-2xl flex items-center justify-center text-white mb-6">
                    {vorteil.icon}
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3">{vorteil.titel}</h3>
                  <p className="text-slate-600">{vorteil.beschreibung}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Zielgruppen Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 bg-purple-100 text-purple-700 rounded-full text-sm font-bold mb-4">
              FÜR WEN IST DAS?
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              Der Intensivkurs ist perfekt für dich, wenn...
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {zielgruppen.map((gruppe, index) => (
              <div key={index} className="text-center p-8 rounded-2xl bg-gradient-to-br from-slate-50 to-slate-100 hover:from-blue-50 hover:to-emerald-50 transition-colors">
                <div className="text-5xl mb-4">{gruppe.icon}</div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">{gruppe.titel}</h3>
                <p className="text-slate-600">{gruppe.beschreibung}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-emerald-600 to-blue-600 relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-dark opacity-10"></div>
        <div className="relative max-w-4xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Bereit für deinen Führerschein?
          </h2>
          <p className="text-xl text-emerald-100 mb-8 max-w-2xl mx-auto">
            Die nächsten Intensivkurse starten bald. Sichere dir jetzt deinen Platz 
            und hol dir deinen Führerschein in nur 7 Tagen!
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/kontakt">
              <Button size="lg" className="bg-white text-emerald-600 hover:bg-emerald-50 text-lg px-10 py-6 rounded-full shadow-xl">
                Kostenlose Beratung
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <a href="tel:+496920326059">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 text-lg px-10 py-6 rounded-full">
                <Phone className="mr-2 w-5 h-5" />
                Jetzt anrufen
              </Button>
            </a>
          </div>
          <p className="text-emerald-200 mt-6 text-sm">
            Keine Wartezeit. Keine versteckten Kosten. Nur Ergebnisse.
          </p>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white" id="faq">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 bg-orange-100 text-orange-700 rounded-full text-sm font-bold mb-4">
              HÄUFIGE FRAGEN
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              Du hast Fragen? Wir haben Antworten.
            </h2>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem key={index} value={`item-${index}`} className="border rounded-xl px-6 shadow-sm">
                <AccordionTrigger className="text-left font-semibold text-lg hover:no-underline py-6">
                  {faq.frage}
                </AccordionTrigger>
                <AccordionContent className="text-slate-600 pb-6">
                  {faq.antwort}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Standorte Section */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">
              Intensivkurs an 4 Standorten
            </h2>
            <p className="text-slate-600">Wähle den Standort in deiner Nähe</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { name: 'Frankfurt Schwanheim', slug: 'frankfurt-schwanheim' },
              { name: 'Frankfurt Niederrad', slug: 'frankfurt-niederrad' },
              { name: 'Offenbach', slug: 'offenbach' },
              { name: 'Bad Homburg', slug: 'bad-homburg' }
            ].map((standort) => (
              <Link 
                key={standort.slug}
                to={`/standorte/${standort.slug}`}
                className="flex items-center gap-3 p-6 bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow"
              >
                <MapPin className="w-5 h-5 text-blue-600" />
                <span className="font-semibold text-slate-900">{standort.name}</span>
                <ArrowRight className="w-4 h-4 text-slate-400 ml-auto" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-slate-900">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Noch unsicher? Lass dich kostenlos beraten!
          </h2>
          <p className="text-slate-400 mb-8">
            Ruf uns an oder schreib uns. Wir beantworten alle deine Fragen - unverbindlich.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="tel:+496920326059" className="flex items-center gap-2 text-emerald-400 hover:text-emerald-300 font-semibold">
              <Phone className="w-5 h-5" />
              069 20 32 60 59
            </a>
            <span className="text-slate-600">|</span>
            <a href="mailto:info@roessnerfahrschule.de" className="text-emerald-400 hover:text-emerald-300 font-semibold">
              info@roessnerfahrschule.de
            </a>
          </div>
        </div>
      </section>

      {/* SEO Schema */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Course",
          "name": "Führerschein Intensivkurs - 7 Tage",
          "description": "Führerschein in nur 7 Tagen mit unserem Intensivkurs. Theorie und Praxis kompakt, 95% Bestehensquote.",
          "provider": {
            "@type": "DrivingSchool",
            "name": "Fahrschule Rößner",
            "url": "https://www.roessnerfahrschule.de"
          },
          "hasCourseInstance": {
            "@type": "CourseInstance",
            "courseMode": "onsite",
            "duration": "P7D",
            "location": {
              "@type": "Place",
              "name": "Fahrschule Rößner",
              "address": {
                "@type": "PostalAddress",
                "addressLocality": "Frankfurt am Main",
                "addressCountry": "DE"
              }
            }
          }
        })
      }} />
    </div>
  );
};

export default IntensivkursSeite;
